/**
 * Guarda Mundo — ajuste dinâmico de font-size para títulos institucionais.
 * data-gm-fit-text="single" | "lines-2" | "lines-3" | "synced-lines" | "forced-lines"
 *
 * Em `.gm-hero__content--fit-reveal`, título + subtítulo compartilham o mesmo
 * tamanho final (min dos máximos CSS × o que a linha mais longa precisa),
 * para manter consistência quando ambos os seletores estão iguais (ex.: Médio).
 */
(function () {
  const SELECTOR = '[data-gm-fit-text]';
  const FIT_SAFETY_PX = 4;

  /**
   * @param {HTMLElement} el
   * @returns {HTMLElement | null}
   */
  function getContainer(el) {
    return (
      el.closest('.gm-sobre-editorial__inner') ||
      el.closest('.gm-sobre-editorial__title-fit') ||
      el.closest('.gm-hero__content') ||
      el.closest('.gm-institutional-overlay-hero__content') ||
      el.closest('.gm-section-heading') ||
      el.closest('.gm-pdp-intro__bolsa-dani') ||
      el.parentElement
    );
  }

  /**
   * @param {HTMLElement} el
   * @returns {HTMLElement | null}
   */
  function getRevealGroup(el) {
    return el.closest('.gm-hero__content--fit-reveal');
  }

  /**
   * @returns {boolean}
   */
  function shouldWaitForFonts() {
    return Boolean(document.fonts && document.fonts.status !== 'loaded');
  }

  /**
   * @param {HTMLElement} container
   * @returns {number}
   */
  function getContentWidth(container) {
    const styles = getComputedStyle(container);
    const paddingX =
      (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);

    return Math.max(0, container.clientWidth - paddingX - FIT_SAFETY_PX);
  }

  /**
   * @param {HTMLElement} el
   */
  function prepareMeasureLayout(el) {
    el.style.whiteSpace = 'nowrap';
    el.style.display = 'inline-block';
    el.style.width = 'auto';
    el.style.maxWidth = 'none';
    el.style.boxSizing = 'border-box';
  }

  /**
   * @param {HTMLElement} el
   */
  function applySingleLineLayout(el) {
    el.style.whiteSpace = 'nowrap';
    el.style.display = 'block';
    el.style.width = '100%';
    el.style.maxWidth = '100%';
    el.style.boxSizing = 'border-box';
  }

  /**
   * @param {HTMLElement} el
   */
  function prepareMultilineLayout(el) {
    el.style.whiteSpace = 'normal';
    el.style.display = 'block';
    el.style.width = '100%';
    el.style.maxWidth = '100%';
    el.style.boxSizing = 'border-box';
    el.style.overflowWrap = 'normal';
    el.style.wordBreak = 'normal';
    el.style.hyphens = 'none';
  }

  /**
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  function isVisible(el) {
    return el.getClientRects().length > 0;
  }

  /**
   * @param {string} value
   * @param {HTMLElement} el
   * @returns {number}
   */
  function parseCssLength(value, el) {
    const raw = String(value || '').trim();
    if (!raw) return NaN;
    const num = parseFloat(raw);
    if (Number.isNaN(num)) return NaN;
    if (raw.endsWith('rem')) {
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return num * rootPx;
    }
    if (raw.endsWith('em')) {
      const baseEl = el.parentElement instanceof HTMLElement ? el.parentElement : el;
      const basePx = parseFloat(getComputedStyle(baseEl).fontSize) || 16;
      return num * basePx;
    }
    return num;
  }

  /**
   * @param {HTMLElement} el
   * @returns {{ max: number; min: number }}
   */
  function getBounds(el) {
    el.style.fontSize = '';
    const styles = getComputedStyle(el);
    const max = parseFloat(styles.fontSize) || 40;
    const minVar = parseCssLength(styles.getPropertyValue('--gm-fit-text-min'), el);
    const min = !Number.isNaN(minVar) ? minVar : Math.max(14, max * 0.35);
    return { max, min: Math.min(min, max) };
  }

  /**
   * @param {HTMLElement} el
   * @param {number} sizePx
   */
  function setSize(el, sizePx) {
    el.style.fontSize = `${sizePx}px`;
  }

  /**
   * @param {HTMLElement} el
   * @returns {number}
   */
  function measureTextWidth(el) {
    return el.scrollWidth;
  }

  /**
   * @param {HTMLElement} el
   * @param {number} available
   * @param {number} max
   * @param {number} min
   * @returns {number}
   */
  function binarySearchFontSize(el, available, max, min) {
    setSize(el, max);
    if (measureTextWidth(el) <= available + 1) {
      return max;
    }

    if (min >= max) {
      return min;
    }

    let low = min;
    let high = max;
    let best = min;

    while (high - low > 0.25) {
      const mid = (low + high) / 2;
      setSize(el, mid);

      if (measureTextWidth(el) <= available + 1) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    setSize(el, best);

    while (measureTextWidth(el) > available + 1 && best > min) {
      best = Math.max(min, best - 0.5);
      setSize(el, best);
    }

    return best;
  }

  /**
   * @param {HTMLElement} el
   */
  function fitSingleLine(el) {
    const container = getContainer(el);
    if (!container || !isVisible(el)) return;

    const available = getContentWidth(container);
    if (available <= 0) return;

    prepareMeasureLayout(el);

    const { max, min } = getBounds(el);
    binarySearchFontSize(el, available, max, min);

    applySingleLineLayout(el);
  }

  /**
   * @param {HTMLElement} el
   * @param {number} maxLines
   */
  function fitLines(el, maxLines) {
    const container = getContainer(el);
    if (!container || !isVisible(el)) return;

    const availableWidth = getContentWidth(container);
    if (availableWidth <= 0) return;

    prepareMultilineLayout(el);

    const { max, min } = getBounds(el);

    let low = min;
    let high = max;
    let best = min;

    while (high - low > 0.25) {
      const mid = (low + high) / 2;
      setSize(el, mid);

      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || mid * 1.05;
      const lines = Math.round(el.scrollHeight / lineHeight);

      if (lines <= maxLines && el.scrollWidth <= availableWidth + 1) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    setSize(el, best);
  }

  /**
   * Linhas forçadas (`.gm-section-title__line`): shrink compartilhado até
   * a linha mais longa caber em 1 faixa (nowrap). Mobile-first; ≥750px no-op.
   * @param {HTMLElement} el
   */
  function fitSyncedLines(el) {
    if (window.matchMedia('(min-width: 750px)').matches) {
      el.style.fontSize = '';
      el.querySelectorAll('.gm-section-title__line').forEach((line) => {
        if (line instanceof HTMLElement) {
          line.style.whiteSpace = '';
          line.style.display = '';
        }
      });
      return;
    }

    const container = getContainer(el);
    if (!container || !isVisible(el)) return;

    const available = getContentWidth(container);
    if (available <= 0) return;

    const lines = [...el.querySelectorAll('.gm-section-title__line')].filter(
      (line) => line instanceof HTMLElement
    );
    if (lines.length === 0) {
      fitLines(el, 3);
      return;
    }

    lines.forEach((line) => prepareMeasureLayout(line));

    const { max, min } = getBounds(el);

    let low = min;
    let high = max;
    let best = min;

    const fits = (sizePx) => {
      setSize(el, sizePx);
      return lines.every((line) => measureTextWidth(line) <= available + 1);
    };

    if (fits(max)) {
      setSize(el, max);
      lines.forEach((line) => applySingleLineLayout(line));
      return;
    }

    while (high - low > 0.25) {
      const mid = (low + high) / 2;
      if (fits(mid)) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    setSize(el, best);
    while (!fits(best) && best > min) {
      best = Math.max(min, best - 0.5);
      setSize(el, best);
    }

    lines.forEach((line) => applySingleLineLayout(line));
  }

  /**
   * Linhas forçadas (spans): mede com width:auto (não 100%), shrink até caber.
   * @param {HTMLElement} el
   */
  function fitForcedLines(el) {
    const container = getContainer(el);
    if (!container || !isVisible(el)) return;

    const available = getContentWidth(container);
    if (available <= 0) return;

    const lines = [
      ...el.querySelectorAll('.gm-hero__subheading-line, .gm-section-title__line'),
    ].filter((line) => line instanceof HTMLElement);

    if (lines.length === 0) {
      fitLines(el, 2);
      return;
    }

    el.style.whiteSpace = 'normal';
    el.style.display = 'block';
    el.style.width = '100%';
    el.style.maxWidth = '100%';
    el.style.boxSizing = 'border-box';

    lines.forEach((line) => prepareMeasureLayout(line));

    const { max, min } = getBounds(el);

    let low = min;
    let high = max;
    let best = min;

    const fits = (sizePx) => {
      setSize(el, sizePx);
      return lines.every((line) => measureTextWidth(line) <= available + 1);
    };

    if (fits(max)) {
      setSize(el, max);
      lines.forEach((line) => applySingleLineLayout(line));
      return;
    }

    while (high - low > 0.25) {
      const mid = (low + high) / 2;
      if (fits(mid)) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    setSize(el, best);
    while (!fits(best) && best > min) {
      best = Math.max(min, best - 0.5);
      setSize(el, best);
    }

    lines.forEach((line) => applySingleLineLayout(line));
  }

  /**
   * Título + subtítulo da hero: um tamanho compartilhado (respeitando o max de cada um).
   * @param {HTMLElement} group
   */
  function fitRevealGroup(group) {
    if (!isVisible(group)) return;

    const available = getContentWidth(group);
    if (available <= 0) return;

    const parents = [...group.querySelectorAll(SELECTOR)].filter(
      (el) => el instanceof HTMLElement
    );
    if (parents.length === 0) return;

    /** @type {{ parent: HTMLElement; line: HTMLElement }[]} */
    const targets = [];
    /** @type {Map<HTMLElement, { max: number; min: number }>} */
    const boundsByParent = new Map();

    parents.forEach((parent) => {
      boundsByParent.set(parent, getBounds(parent));
      const mode = parent.getAttribute('data-gm-fit-text');

      if (mode === 'forced-lines' || mode === 'synced-lines') {
        const lines = [
          ...parent.querySelectorAll('.gm-hero__subheading-line, .gm-section-title__line'),
        ].filter((line) => line instanceof HTMLElement);

        if (lines.length === 0) {
          targets.push({ parent, line: parent });
          prepareMeasureLayout(parent);
          return;
        }

        parent.style.whiteSpace = 'normal';
        parent.style.display = 'block';
        parent.style.width = '100%';
        parent.style.maxWidth = '100%';
        parent.style.boxSizing = 'border-box';

        lines.forEach((line) => {
          prepareMeasureLayout(line);
          targets.push({ parent, line });
        });
        return;
      }

      prepareMeasureLayout(parent);
      targets.push({ parent, line: parent });
    });

    const globalMax = Math.max(...[...boundsByParent.values()].map((b) => b.max));
    const globalMin = Math.min(...[...boundsByParent.values()].map((b) => b.min));

    const applyCandidate = (sizePx) => {
      parents.forEach((parent) => {
        const bounds = boundsByParent.get(parent);
        setSize(parent, Math.min(sizePx, bounds ? bounds.max : sizePx));
      });
    };

    const fits = (sizePx) => {
      applyCandidate(sizePx);
      return targets.every(({ line }) => measureTextWidth(line) <= available + 1);
    };

    let best = globalMin;

    if (fits(globalMax)) {
      best = globalMax;
    } else {
      let low = globalMin;
      let high = globalMax;

      while (high - low > 0.25) {
        const mid = (low + high) / 2;
        if (fits(mid)) {
          best = mid;
          low = mid;
        } else {
          high = mid;
        }
      }

      applyCandidate(best);
      while (!fits(best) && best > globalMin) {
        best = Math.max(globalMin, best - 0.5);
        applyCandidate(best);
      }
    }

    applyCandidate(best);

    parents.forEach((parent) => {
      const mode = parent.getAttribute('data-gm-fit-text');
      if (mode === 'forced-lines' || mode === 'synced-lines') {
        parent
          .querySelectorAll('.gm-hero__subheading-line, .gm-section-title__line')
          .forEach((line) => {
            if (line instanceof HTMLElement) applySingleLineLayout(line);
          });
      } else {
        applySingleLineLayout(parent);
      }
    });
  }

  /**
   * @param {HTMLElement} el
   */
  function fitElement(el) {
    const group = getRevealGroup(el);
    if (group instanceof HTMLElement) {
      fitRevealGroup(group);
      return;
    }

    const mode = el.getAttribute('data-gm-fit-text');
    if (mode === 'lines-2') {
      fitLines(el, 2);
    } else if (mode === 'lines-3') {
      fitLines(el, 3);
    } else if (mode === 'synced-lines') {
      fitSyncedLines(el);
    } else if (mode === 'forced-lines') {
      fitForcedLines(el);
    } else {
      fitSingleLine(el);
    }
  }

  /**
   * Revela título + subtítulo da hero após o fit.
   * @param {HTMLElement} el
   */
  function scheduleReveal(el) {
    const group = getRevealGroup(el);
    if (!(group instanceof HTMLElement)) return;
    if (group.classList.contains('gm-fit-reveal--ready')) return;
    if (shouldWaitForFonts()) return;

    const reveal = () => {
      if (group.classList.contains('gm-fit-reveal--ready')) return;
      group.classList.add('gm-fit-reveal--ready');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      reveal();
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(reveal);
    });
  }

  /** @type {WeakMap<HTMLElement, ResizeObserver>} */
  const observers = new WeakMap();

  /**
   * @param {HTMLElement} el
   */
  function bindElement(el) {
    const container = getContainer(el);
    if (!container) return;

    const run = () => {
      const group = getRevealGroup(el);
      if (group instanceof HTMLElement) {
        fitRevealGroup(group);
        scheduleReveal(el);
        return;
      }
      fitElement(el);
      scheduleReveal(el);
    };

    if (!observers.has(el)) {
      const ro = new ResizeObserver(run);
      ro.observe(container);
      observers.set(el, ro);
    }

    run();
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      if (el instanceof HTMLElement) bindElement(el);
    });
  }

  function refitAll() {
    /** @type {Set<HTMLElement>} */
    const groupsDone = new Set();

    document.querySelectorAll(SELECTOR).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const group = getRevealGroup(el);
      if (group instanceof HTMLElement) {
        if (groupsDone.has(group)) {
          scheduleReveal(el);
          return;
        }
        groupsDone.add(group);
        fitRevealGroup(group);
        scheduleReveal(el);
        return;
      }
      fitElement(el);
      scheduleReveal(el);
    });
  }

  /**
   * Theme editor: section HTML é trocado no save de setting; re-bind + reveal.
   * @param {Event} event
   */
  function onSectionLoad(event) {
    const root = event.target;
    if (!(root instanceof Element)) {
      initAll();
      return;
    }
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const group = getRevealGroup(el);
      if (group instanceof HTMLElement) {
        group.classList.remove('gm-fit-reveal--ready');
      }
      bindElement(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (document.fonts?.ready) {
    document.fonts.ready.then(refitAll);
  }

  window.addEventListener('resize', refitAll, { passive: true });
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:reorder', initAll);
})();
